<template>
  <b-modal
    v-model="data.showModal"
    size="xxl"
    button-size="sm"
    modal-class="edit-export"
    title-class="w-100"
    footer-class="w-100"
    no-fade
    @show="onShow"
    @hide="onHide"
    scrollable
  >
    <!-- Modal header -->
    <div slot="modal-title">
      <div class="d-flex w-100 justify-content-between">
        <span v-if="exportDetails">
          Export - {{ exportDetails.title }}
        </span>
        <span v-else>Export</span>
      </div>
    </div>

    <!-- Modal content -->
    <b-card no-body class="mb-3">
      <b-tabs pills card v-model="activeTab" content-class="d-none">
        <b-tab title="1. Details"></b-tab>
        <b-tab title="2. Fields"></b-tab>
        <b-tab title="3. Sorting"></b-tab>
      </b-tabs>
    </b-card>

    <div v-if="exportDetails !== null && activeTab === 0">
      <b-form-group label-cols="1" label-size="sm" label="Name">
          <b-form-input
            size="sm"
            v-model="exportDetails.title"
            autocomplete="off"
          ></b-form-input>
        </b-form-group>
    </div>

    <div v-if="exportFields !== null && activeTab === 1">
      <div class="d-flex gap-4 w-100">
        <div class="d-flex flex-column gap-2 text-nowrap">
          <draggable
            tag="div"
            :list="exportFields"
            class="d-flex flex-column flex-wrap gap-2"
            handle=".drag-handle"
            @change="updateFieldsOrder()"
          >
            <div
              v-for="(exportField, index) in exportFields"
              :key="exportField.uuid"
              class="d-flex gap-2 align-items-center"
            >
              <div class="drag-handle">
                <i class="fas fa-arrows-alt"></i>
              </div>
              <b-btn
                :variant="index === selectedFieldIndex ? 'primary' : 'white'"
                size="sm"
                @click="selectField(index)"
                class="w-100 text-left"
              >
                <div class="d-flex gap-4 justify-content-between">
                  <span>
                    <span v-if="exportField.field_name.length === 0">New field</span>
                    <span v-else>{{ exportField.field_name }}</span>
                  </span>
                  <span class="d-flex gap-2">
                    <i
                      class="fas fa-eye-slash"
                      :class="{ 'invisible': exportField.is_exported }"
                    ></i>
                    <i
                      class="fas fa-filter"
                      :class="{ 'invisible': !exportField.is_filterable }"
                    ></i>
                  </span>
                </div>
              </b-btn>
            </div>
          </draggable>

          <b-btn
            variant="white"
            size="sm"
            @click="addField()"
            class="w-100"
          >
            Add field
          </b-btn>
        </div>
        <div class="w-100">
          <div v-if="selectedField" class="d-flex flex-column">
            <b-form-group label-cols="3" label-size="sm" label="Field Name">
              <b-form-input
                size="sm"
                v-model="selectedField.field_name"
                autocomplete="off"
              ></b-form-input>
            </b-form-group>

            <b-form-group label-cols="3" label-size="sm" label="Node Path">
              <b-form-input
                size="sm"
                v-model="selectedField.node_xpath"
                autocomplete="off"
              ></b-form-input>
              <small
                v-if="exportPreview?.length >= 0 && exportPreview[0].id === selectedField.id"
                class="mt-1"
              >
                <span class="text-muted small">
                  {{ exportPreview[0].field_value || 'No preview data' }}
                </span>
              </small>
            </b-form-group>

            <b-form-group label-cols="3" label-size="sm" label="Match OP">
              <button-select
                size="sm"
                v-model="selectedField.match_op"
                :options="options.match_op"
              />
            </b-form-group>

            <b-form-group label-cols="3" label-size="sm" label="Field Type">
              <button-select
                size="sm"
                v-model="selectedField.field_type"
                :options="options.field_type"
              />
            </b-form-group>

            <b-form-group
              v-if="selectedField.field_type === 'boolean'"
              label-cols="3"
              label-size="sm"
              label="Boolean (True)"
            >
              <b-form-input
                size="sm"
                v-model="selectedField.boolean_true_text"
              ></b-form-input>
            </b-form-group>

            <b-form-group
              v-if="selectedField.field_type === 'boolean'"
              label-cols="3"
              label-size="sm"
              label="Boolean (False)"
            >
              <b-form-input
                size="sm"
                v-model="selectedField.boolean_false_text"
              ></b-form-input>
            </b-form-group>

            <b-form-group label-cols="3" label-size="sm" label="Is Exported">
              <button-select
                size="sm"
                v-model="selectedField.is_exported"
                :options="options.boolean"
              />
            </b-form-group>

            <b-form-group label-cols="3" label-size="sm" label="Is Filterable">
              <button-select
                size="sm"
                v-model="selectedField.is_filterable"
                :options="options.boolean"
              />
            </b-form-group>

            <b-form-group label-cols="3" label-size="sm" label="Excel Alignment">
              <button-select
                size="sm"
                v-model="selectedField.excel_alignment"
                :options="options.excel_alignment"
              />
            </b-form-group>

            <b-form-group label-cols="3" label-size="sm" label="Excel Format">
              <button-select
                size="sm"
                v-model="selectedField.excel_format"
                :options="options.excel_format"
              />
            </b-form-group>

            <div>
              <b-btn
                variant="danger"
                size="sm"
                @click="deleteField(selectedField)"
              >
                Delete field
              </b-btn>
            </div>
          </div>
        </div>
        <div class="w-50">
          <pre v-highlightjs="data.nodeString"><code class="language-xml"></code></pre>
        </div>
      </div>
    </div>

    <div v-if="exportFields !== null && activeTab === 2">
      <edit-sorting v-model="exportFields" />
    </div>

    <!-- Modal footer -->
    <div slot="modal-footer" class="w-100 d-flex gap-4 justify-content-between align-items-center">
      <div class="d-flex w-100 justify-content-between">
        <div></div>
        <div class="d-flex gap-2">
           <b-btn
            size="sm"
            variant="danger"
            :disabled="isLoading || !canSave"
            @click="onDelete()"
          >
            Delete
          </b-btn>
          <b-btn
            size="sm"
            variant="primary"
            :disabled="isLoading || !canSave"
            @click="onSave()"
          >
            Save
          </b-btn>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import draggable from 'vuedraggable';

const ButtonSelect = () => import('@/components/global/ButtonSelect.vue');
const EditSorting = () => import('@/components/feed_export/EditSorting.vue');

export default {
  name: 'EditExport',
  components: {
    draggable,
    ButtonSelect,
    EditSorting,
  },
  props: {
    data: Object,
  },
  computed: {
    isLoading() {
      return this.loadingCount > 0;
    },
    canSave() {
      return !this.isLoading;
    },
    preventMessage() {
      return 'You have unsaved changes. Are you sure you want to close?';
    },
    preventClose() {
      return this.preventClosing;
    },
    selectedField() {
      if (this.exportFields === null || this.exportFields.length === 0) {
        return null;
      }
      return this.exportFields[this.selectedFieldIndex];
    },
  },
  data() {
    return {
      preventClosing: true,
      loadingCount: 0,
      filters: {},
      options: {
        field_type: [
          { text: 'Text', value: 'text' },
          { text: 'Boolean', value: 'boolean' },
        ],
        match_op: [
          { text: 'First', value: 'first' },
          { text: 'Last', value: 'last' },
          { text: 'Min', value: 'min' },
          { text: 'Max', value: 'max' },
        ],
        excel_alignment: [
          { text: 'Left', value: 'left' },
          { text: 'Center', value: 'center' },
          { text: 'Right', value: 'right' },
        ],
        excel_format: [
          { text: 'Default', value: null },
          { text: 'Number (1 000)', value: 'number' },
          { text: 'Price (1 000,00)', value: 'price' },
        ],
        boolean: [
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ],
      },
      exportDetails: null,
      exportFields: null,
      exportPreview: null,
      selectedFieldIndex: 0,
      activeTab: 0,
    };
  },
  methods: {
    updateFieldsOrder() {
      this.$nextTick().then(() => {
        for (let i = 0; i < this.exportFields.length; i++) {
          this.exportFields[i].field_order = i + 1;
        }
      });
    },
    selectField(index) {
      this.selectedFieldIndex = index;
      this.fetchPreview();
    },
    addField() {
      const newField = {
        id: null,
        field_name: '',
        field_type: 'text',
        field_order: this.exportFields.length + 1,
        node_xpath: '',
        match_op: 'first',
        boolean_true_text: 'True',
        boolean_false_text: 'False',
        is_exported: true,
        is_filterable: false,
        sort_index: null,
        sort_direction: 'asc',
        excel_alignment: 'left',
        excel_format: null,
        uuid: self.crypto.randomUUID(),
      };
      this.exportFields.push(newField);
      this.selectedFieldIndex = this.exportFields.length - 1;
    },
    deleteField(deleteField) {
      if (!confirm(`Do you really wish to delete field '${deleteField.field_name}'?`)) {
        return;
      }
      this.exportFields = this.exportFields.filter((field) => field.uuid !== deleteField.uuid);
      this.selectedFieldIndex = Math.max(0, this.selectedFieldIndex - 1);
    },
    fetchPreview() {
      if (this.data.nodeString === null || this.selectedField === null) {
        this.exportPreview = null;
        return;
      }
      this.loadingCount++;
      this.$http
        .post('/feed_export/preview')
        .send({ nodeString: this.data.nodeString, fields: [this.selectedField] })
        .then((res) => {
          this.exportPreview = res.body;
        })
        .catch((err) => {
          alert(`Failed to fetch preview: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchExportFields() {
      this.loadingCount++;
      this.$http
        .get(`/feed_export/${this.data.feedId}/exports/${this.data.exportId}/fields`)
        .then((res) => {
          this.exportFields = res.body.map((field) => {
            field.uuid = self.crypto.randomUUID();
            return field;
          });
          this.activeTab = this.exportFields.length > 0 ? 1 : 0;
        })
        .catch((err) => {
          alert(`Failed to fetch export fields: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchExportDetails() {
      this.loadingCount++;
      this.$http
        .get(`/feed_export/${this.data.feedId}/exports/${this.data.exportId}`)
        .then((res) => {
          this.exportDetails = res.body;
        })
        .catch((err) => {
          alert(`Failed to fetch export details: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    saveExportDetails() {
      this.loadingCount++;
      this.$http
        .put(`/feed_export/${this.data.feedId}/exports/${this.data.exportId}`)
        .send({ details: this.exportDetails })
        .then(() => {
          this.onSaved();
        })
        .catch((err) => {
          alert(`Failed to fetch update details: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    saveExportFields() {
      this.loadingCount++;
      this.$http
        .put(`/feed_export/${this.data.feedId}/exports/${this.data.exportId}/fields`)
        .send({ fields: this.exportFields })
        .then(() => {
          this.onSaved();
        })
        .catch((err) => {
          alert(`Failed to fetch update fields: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchAll() {
      this.fetchExportDetails();
      this.fetchExportFields();
    },
    onSaved() {
      setTimeout(() => {
        if (this.loadingCount === 0) {
          this.preventClosing = false;
          this.onClose();
        }
      }, 500);
    },
    onSave() {
      this.saveExportDetails();
      this.saveExportFields();
    },
    onDelete() {
      if (!confirm('Are you sure you want to delete this export?')) {
        return;
      }
      this.loadingCount++;
      this.$http
        .delete(`/feed_export/${this.data.feedId}/exports/${this.data.exportId}`)
        .then(() => {
          this.preventClosing = false;
          this.onClose();
        })
        .catch((err) => {
          alert(`Failed to delete export: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    onShow() {
      this.fetchAll();
    },
    onClose() {
      this.data.showModal = false;
      this.$emit('close');
    },
    onHide(event) {
      if (this.preventClose && !confirm(this.preventMessage)) {
        event.preventDefault();
        return;
      }
      this.data.feedId = null;
      this.data.exportId = null;
      this.data.nodeString = null;
      this.exportDetails = null;
      this.exportFields = null;
      this.exportPreview = null;
      this.selectedFieldIndex = 0;
      this.activeTab = 0;
      this.preventClosing = true;
    },
  },
  watch: {
    exportFields: {
      deep: true,
      handler() {
        this.fetchPreview();
      },
    },
  },
};
</script>

<style lang="scss">
  .edit-export {
    .card-header {
      border-bottom: 0;
    }
  }
</style>
